import { useEffect, useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Eyebrow, EASE } from "@/components/Reveal";
import { scrollToId } from "@/App";

const PIECES = [
  {
    brand: "Patek Philippe",
    model: "Nautilus",
    ref: "Réf. 5726 — Acier, quantième annuel, phase de lune",
    text: "Le boîtier hublot dessiné par Gérald Genta, le cadran à relief horizontal. La plus convoitée des montres sport-chic — et l'une des plus difficiles à obtenir.",
    img: "/images/gallery-nautilus.jpg",
    pos: "object-[60%_center]",
    zoom: 1,
  },
  {
    brand: "Rolex",
    model: "Cosmograph Daytona",
    ref: "Réf. 116519LN — Or gris, bracelet Oysterflex",
    text: "Le chronographe de légende. Lunette Cerachrom, compteurs noirs, précision chronométrique. En boutique, l'attente se compte en années.",
    img: "/images/gallery-daytona.jpg",
    pos: "object-center",
    zoom: 1.3,
    shift: "12%",
  },
  {
    brand: "Audemars Piguet",
    model: "Royal Oak",
    ref: "Lunette octogonale — Cadran Tapisserie",
    text: "Huit vis hexagonales, une lunette octogonale, un bracelet intégré. Depuis 1972, la montre qui a réinventé le luxe en acier.",
    img: "/images/gallery-royaloak.jpg",
    pos: "object-[70%_center]",
    zoom: 1,
  },
  {
    brand: "Rolex",
    model: "Submariner",
    ref: "Submariner Date — Réf. 126613LB, or & acier, cadran bleu",
    text: "La montre de plongée de référence depuis 1953. Lunette tournante unidirectionnelle Cerachrom, cadran laqué, index luminescents. L'icône absolue.",
    img: "/images/gallery-submariner.jpg",
    pos: "object-center",
    zoom: 1.35,
    shift: "13%",
  },
  {
    brand: "Rolex",
    model: "Datejust",
    ref: "Réf. 126334 — Lunette cannelée, bracelet Jubilee",
    text: "L'archétype de la montre classique depuis 1945. Lunette cannelée, loupe Cyclope, bracelet Jubilee — l'élégance Rolex dans sa forme la plus pure.",
    img: "/images/gallery-datejust.jpg",
    pos: "object-[center_45%]",
    zoom: 1.1,
  },
  {
    brand: "Omega",
    model: "Seamaster",
    ref: "Diver 300M — Cadran vagues, lunette céramique",
    text: "Le cadran aux vagues laquées, la lunette bleue, l'écusson de la Maison. Une icône de la plongée, portée à l'écran depuis trois décennies.",
    img: "/images/gallery-seamaster.jpg",
    pos: "object-[center_40%]",
    zoom: 1,
  },
];

const letterVariants = {
  hidden: { opacity: 0, y: 18 },
  show: (i) => ({ opacity: 1, y: 0, transition: { duration: 0.9, ease: EASE, delay: 0.3 + i * 0.045 } }),
};

const Letters = ({ text }) => {
  let idx = 0;
  return (
    <span aria-label={text}>
      {text.split(" ").map((word, w) => (
        <span key={word} className="inline-block whitespace-nowrap mr-[0.28em] last:mr-0">
          {[...word].map((c, ci) => (
            <motion.span key={`${word}-${ci}`} custom={idx++} variants={letterVariants} className="inline-block">
              {c}
            </motion.span>
          ))}
        </span>
      ))}
    </span>
  );
};

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  show: (d) => ({ opacity: 1, y: 0, transition: { duration: 1, ease: EASE, delay: d } }),
};

const IntroPanel = ({ progress, step }) => {
  const opacity = useTransform(progress, [0, step], [1, 0.15]);
  const scale = useTransform(progress, [0, step], [1, 0.96]);
  return (
    <div className="sticky top-0 h-screen overflow-hidden bg-obsidian" data-testid="gallery-intro">
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            "radial-gradient(ellipse 55% 45% at 50% 60%, rgba(197,160,89,0.12), transparent 70%)",
        }}
      />
      <motion.div
        style={{ opacity, scale }}
        className="relative h-full max-w-7xl mx-auto px-5 sm:px-8 flex flex-col justify-center"
      >
        <motion.div initial="hidden" whileInView="show" viewport={{ amount: 0.5 }} className="max-w-3xl">
          <motion.div variants={fadeUp} custom={0}>
            <Eyebrow>Modèles recherchés</Eyebrow>
          </motion.div>
          <h2 className="mt-8 font-display text-4xl sm:text-5xl lg:text-7xl leading-[1.02] text-ivory">
            <motion.span variants={fadeUp} custom={0.15} className="block">
              Des garde-temps d'exception,
            </motion.span>
            <motion.span variants={fadeUp} custom={0.3} className="block italic text-gold">
              recherchés à la demande.
            </motion.span>
          </h2>
          <motion.p
            variants={fadeUp}
            custom={0.45}
            className="mt-10 max-w-md text-sm sm:text-base text-stone2 leading-relaxed"
            data-testid="models-disclaimer"
          >
            Ceci n'est pas un catalogue. Il s'agit d'exemples de garde-temps pouvant faire l'objet
            d'une recherche, selon disponibilité du marché.
          </motion.p>
          <motion.p
            variants={fadeUp}
            custom={0.6}
            className="mt-14 font-mono text-[10px] tracking-[0.35em] uppercase text-stone2/50"
          >
            Défiler pour découvrir
          </motion.p>
        </motion.div>
      </motion.div>
    </div>
  );
};

const Panel = ({ piece, i, n, progress, step }) => {
  const enterStart = (i - 1) * step;
  const enterEnd = i * step;
  const leaveEnd = (i + 1) * step;

  const scale = useTransform(progress, [enterStart, enterEnd, leaveEnd], [1.22 * piece.zoom, piece.zoom, 0.95 * piece.zoom]);
  const veil = useTransform(
    progress,
    [enterStart, enterStart + step * 0.5, enterEnd, leaveEnd],
    [0.92, 0.6, 0.22, 0.88]
  );
  const glow = useTransform(progress, [enterStart + step * 0.35, enterEnd, leaveEnd], [0, 1, 0]);
  const imgY = useTransform(progress, [enterStart, leaveEnd], ["-6%", "6%"]);

  return (
    <div className="sticky top-0 h-screen overflow-hidden bg-[#07070a]" data-testid={`gallery-panel-${i}`}>
      <motion.img
        src={piece.img}
        alt={`${piece.brand} ${piece.model}`}
        style={{ scale, y: imgY, x: piece.shift || "0%" }}
        className={`absolute inset-0 w-full h-full object-cover ${piece.pos}`}
      />
      <motion.div style={{ opacity: veil }} className="absolute inset-0 bg-[#07070a] pointer-events-none" />
      <motion.div
        style={{
          opacity: glow,
          background:
            "radial-gradient(ellipse 38% 48% at 68% 48%, rgba(223,186,106,0.20), transparent 70%)",
        }}
        className="absolute inset-0 pointer-events-none mix-blend-screen"
      />
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            "linear-gradient(90deg, rgba(7,7,10,0.86) 0%, rgba(7,7,10,0.55) 38%, rgba(7,7,10,0.05) 70%), linear-gradient(0deg, rgba(7,7,10,0.85) 0%, transparent 35%, transparent 75%, rgba(7,7,10,0.55) 100%)",
        }}
      />

      <motion.div
        initial="hidden"
        whileInView="show"
        viewport={{ amount: 0.55 }}
        className="relative h-full max-w-7xl mx-auto px-5 sm:px-8 flex items-end sm:items-center pb-24 sm:pb-0"
      >
        <div className="max-w-xl">
          <motion.p
            variants={fadeUp}
            custom={0}
            className="font-mono text-[10px] tracking-[0.35em] uppercase text-gold/80"
          >
            {String(i).padStart(2, "0")} — {String(n).padStart(2, "0")}
          </motion.p>
          <motion.p
            variants={fadeUp}
            custom={0.15}
            className="mt-7 font-sans text-xs sm:text-sm tracking-[0.5em] uppercase text-ivory/80"
            data-testid={`gallery-brand-${i}`}
          >
            {piece.brand}
          </motion.p>
          <h3
            className="mt-4 font-display text-[2.6rem] sm:text-6xl lg:text-8xl leading-[0.98] tracking-[0.06em] text-ivory [text-shadow:0_2px_40px_rgba(0,0,0,0.9)]"
            data-testid={`gallery-model-${i}`}
          >
            <Letters text={piece.model} />
          </h3>
          <motion.p
            variants={fadeUp}
            custom={0.55}
            className="mt-6 font-mono text-[10px] tracking-[0.25em] uppercase text-gold/70"
          >
            {piece.ref}
          </motion.p>
          <motion.p
            variants={fadeUp}
            custom={0.65}
            className="mt-6 text-sm sm:text-base lg:text-lg text-ivory/75 leading-relaxed [text-shadow:0_1px_18px_rgba(0,0,0,0.9)]"
          >
            {piece.text}
          </motion.p>
          <motion.div variants={fadeUp} custom={0.8} className="mt-10 flex items-center gap-8">
            <button
              onClick={() => scrollToId("demande")}
              className="group inline-flex items-center gap-4 text-[11px] tracking-[0.3em] uppercase text-gold pointer-events-auto"
              data-testid={`gallery-cta-${i}`}
            >
              <span className="h-px w-10 bg-gold/70 transition-all duration-500 group-hover:w-16" />
              Rechercher ce modèle
            </button>
          </motion.div>
        </div>
      </motion.div>
    </div>
  );
};

export default function Gallery() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end end"] });
  const n = PIECES.length;
  const step = 1 / n;
  useEffect(() => {
    PIECES.forEach((p) => { const im = new Image(); im.src = p.img; });
  }, []);

  return (
    <section id="recherches" ref={ref} className="relative" data-testid="gallery-section">
      <IntroPanel progress={scrollYProgress} step={step} />
      {PIECES.map((p, idx) => (
        <Panel key={p.model} piece={p} i={idx + 1} n={n} progress={scrollYProgress} step={step} />
      ))}
    </section>
  );
}
